import { motion } from "motion/react";

export default function BentoGrid() {
  const features = [
    {
      icon: "📷",
      title: "Instant Plant Recognition",
      desc: "Upload a photo and identify any plant, flower, tree or leaf within seconds using advanced AI technology.",
      badge: "AI Engine",
      glowColor: "rgba(34,197,94,0.12)",
      borderColor: "hover:border-green-300/60",
    },
    {
      icon: "🌿",
      title: "Detailed Plant Information",
      desc: "Get scientific names, care guides, watering schedules, toxicity information and growth requirements.",
      badge: "Database",
      glowColor: "rgba(16,185,129,0.12)",
      borderColor: "hover:border-emerald-300/60",
    },
    {
      icon: "❤️",
      title: "Save Your Collection",
      desc: "Create your personal plant library and revisit previously identified plants anytime.",
      badge: "Vault",
      glowColor: "rgba(239,68,68,0.08)",
      borderColor: "hover:border-red-205/40",
    },
    {
      icon: "⚡",
      title: "Fast AI Results",
      desc: "Receive highly accurate plant identification results in just a few seconds.",
      badge: "Realtime",
      glowColor: "rgba(59,130,246,0.1)",
      borderColor: "hover:border-blue-300/60",
    },
  ];

  return (
    <section id="features" className="py-32 bg-white relative overflow-hidden">
      
      {/* Decorative Background Glows & Organic Soft Shapes */}
      <div className="absolute top-1/4 left-1/10 w-[500px] h-[500px] bg-green-500/[0.025] rounded-full blur-[120px] pointer-events-none -z-10" />
      <div className="absolute bottom-1/4 right-1/10 w-[600px] h-[600px] bg-emerald-500/[0.025] rounded-full blur-[140px] pointer-events-none -z-10" />
      
      <div className="max-w-7xl mx-auto px-6 relative z-15">
        
        {/* Section Title Area / Header */}
        <div className="text-center max-w-3xl mx-auto mb-20 space-y-5">
          
          {/* Small Badge */}
          <motion.div
            initial={{ opacity: 0, y: 15 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5 }}
            className="inline-flex items-center gap-1.5 px-3.5 py-1 bg-green-50/70 border border-green-150/40 rounded-full text-green-700 text-xs font-extrabold uppercase tracking-widest shadow-xs"
          >
            <span className="text-[11px]">✨</span> Powerful Features
          </motion.div>

          {/* Main Heading */}
          <motion.h2
            initial={{ opacity: 0, y: 15 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5, delay: 0.1 }}
            className="text-4xl sm:text-5xl font-[900] text-gray-950 tracking-tight leading-[1.12]"
          >
            Core AI Specifications <br className="hidden sm:inline" />
            & Platform Features
          </motion.h2>

          {/* Subheading */}
          <motion.p
            initial={{ opacity: 0, y: 15 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5, delay: 0.2 }}
            className="text-lg text-gray-500 font-medium max-w-[700px] mx-auto leading-relaxed"
          >
            Advanced AI-powered tools designed for gardeners, students, researchers, farmers and plant lovers.
          </motion.p>
        </div>

        {/* Feature Grid: 4 columns on desktop, 2 on tablet, 1 on mobile */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
          {features.map((feature, idx) => (
            <motion.div
              key={idx}
              initial={{ opacity: 0, y: 25 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-50px" }}
              transition={{ duration: 0.6, delay: idx * 0.1, ease: [0.16, 1, 0.3, 1] }}
              whileHover={{ y: -6 }}
              className={`group bg-white/70 backdrop-blur-md border border-gray-150 rounded-3xl p-7 flex flex-col justify-between transition-all duration-300 relative overflow-hidden cursor-pointer shadow-xs hover:shadow-xl ${feature.borderColor}`}
            >
              {/* Subtle local glowing gradient behind item on card hover */}
              <div 
                className="absolute inset-x-0 bottom-0 h-40 bg-radial transition-opacity duration-300 opacity-0 group-hover:opacity-100 pointer-events-none -z-10"
                style={{
                  backgroundImage: `radial-gradient(circle 120px at 50% 120%, ${feature.glowColor}, transparent)`
                }}
              />

              <div>
                {/* Floating Icon Wrapper container with gentle floating motion */}
                <motion.div 
                  animate={{ 
                    y: [0, -3, 0],
                  }}
                  transition={{ 
                    repeat: Infinity, 
                    duration: 4 + idx, 
                    ease: "easeInOut" 
                  }}
                  className="w-14 h-14 rounded-2xl bg-gray-50 group-hover:bg-green-50/60 border border-gray-100 group-hover:border-green-150/40 flex items-center justify-center text-2xl shadow-xs transition-colors duration-300 mb-7"
                >
                  {feature.icon}
                </motion.div>

                {/* Card Title */}
                <h3 className="text-xl font-extrabold text-gray-950 tracking-tight leading-snug group-hover:text-green-700 transition-colors duration-250">
                  {feature.title}
                </h3>

                {/* Card Description */}
                <p className="mt-3.5 text-sm text-gray-500 font-medium leading-relaxed">
                  {feature.desc}
                </p>
              </div>

              {/* Small Category Meta tags for aesthetic Stripe-ish finish */}
              <div className="mt-8 pt-4 border-t border-gray-100/50 flex items-center justify-between">
                <span className="text-[10px] text-gray-400 font-bold uppercase tracking-wider">
                  {feature.badge}
                </span>
                <span className="text-xs text-gray-300 group-hover:text-green-500 group-hover:translate-x-1 transition-all duration-250 font-bold">
                  Explore →
                </span>
              </div>

            </motion.div>
          ))}
        </div>

      </div>
    </section>
  );
}
